import React from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftIcon,
} from "@heroicons/react/24/outline";
import ThemedButton from "../Shared/ThemedButton";
import PolicySections from "./PolicySections";
import { getSetPolicies } from "./policyMeta";
import { getBookingPageTheme } from "../../utility/themeUtils";
import {
  formatDateTime,
  getLocationDisplay,
  formatDuration,
} from "../../utility/bookingUtils";
import { formatPriceWithCurrency, parsePrice } from "../../utility/priceUtils";
import { getTotalServiceDuration, calculateBookingTotals } from "../../utility/bookingFlowUtils";
import { useBookingFlow } from "../../contexts/BookingFlowContext";

const BookingConfirmationPage = () => {
  const {
    bookingPage,
    bookingResult,
    selectedServices,
    selectedDate,
    selectedTime,
    selectedAppointmentType,
    selectedAddress,
    selectedLocation,
    fixedLocationData,
    contactFormData,
    currency,
    travelFee,
    outsideAvailabilityRequested,
    customTimeRequested,
    handleResetBookingFlow,
  } = useBookingFlow();

  const { primaryColor } = getBookingPageTheme(bookingPage);
  const managementUrl = bookingResult?.managementUrl;

  const isConfirmed = bookingResult?.status === "confirmed";
  const contactEmail =
    contactFormData?.email || bookingResult?.appointmentDetails?.client?.email;

  const totalDuration = getTotalServiceDuration(selectedServices);

  const { serviceTotal, totalPrice: computedTotal, applicableTravelFee } = calculateBookingTotals(
    selectedServices,
    selectedAppointmentType,
    bookingPage?.travelFeesEnabled,
    travelFee
  );

  // Use server-provided total for confirmed bookings (source of truth);
  // fall back to client-side calculation for pending requests
  const displayTotal = isConfirmed && bookingResult?.appointmentDetails?.totalPrice != null
    ? parsePrice(bookingResult.appointmentDetails.totalPrice)
    : computedTotal;

  const approvalReasons = (() => {
    const reasons = [];
    if (customTimeRequested) {
      reasons.push("Custom time slot requested");
    }
    if (outsideAvailabilityRequested) {
      reasons.push("Location is outside the regular service area");
    }
    return reasons;
  })();

  return (
    <div className="py-8">
      <div className="max-w-lg mx-auto bg-themed-card border-themed rounded-card shadow-hairline border p-8 sm:p-10">
        {/* Status Hero */}
        <div className="text-center">
          <div
            className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5 border ${
              isConfirmed
                ? "border-signal-green-500 text-signal-green-500"
                : "border-signal-amber-500 text-signal-amber-500"
            }`}
          >
            {isConfirmed ? (
              <CheckCircleIcon className="w-6 h-6" strokeWidth={1.5} />
            ) : (
              <ClockIcon className="w-6 h-6" strokeWidth={1.5} />
            )}
          </div>
          {bookingPage?.businessName && (
            <p className="smallcaps text-themed-secondary mb-2 break-words">{bookingPage.businessName}</p>
          )}
          <h3 className="font-themed-display text-3xl text-themed-primary mb-2">
            {isConfirmed ? "You're booked." : "Request received."}
          </h3>
          <p className="text-sm text-themed-secondary">
            {isConfirmed
              ? `A confirmation email has been sent to ${contactEmail}`
              : `We'll review your request and send confirmation to ${contactEmail}`}
          </p>
          {bookingResult?.appointmentDetails?._id && (
            <p className="text-xs text-themed-secondary mt-3 tabular">
              Booking ref · <span className="font-mono">{String(bookingResult.appointmentDetails._id).slice(0, 8).toUpperCase()}</span>
            </p>
          )}
        </div>

        {/* Details */}
        <div className="border-t border-themed pt-6 mt-6 space-y-3">
          {/* Date & Time */}
          <div className="flex items-start space-x-3">
            <CalendarDaysIcon className="h-5 w-5 text-themed-secondary mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium text-themed-primary">
              {formatDateTime(selectedDate, selectedTime)}
            </p>
          </div>

          {/* Location */}
          <div className="flex items-start space-x-3">
            <MapPinIcon className="h-5 w-5 text-themed-secondary mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium text-themed-primary">
              {getLocationDisplay(
                selectedAppointmentType,
                selectedAddress,
                selectedLocation,
                fixedLocationData
              )}
            </p>
          </div>

          {/* Services */}
          {selectedServices.length > 0 && selectedServices.map((item, index) => (
            <div key={index} className="flex justify-between items-start text-sm">
              <div>
                <span className="text-themed-primary">
                  {item.quantity > 1 && `${item.quantity}x `}{item.service.name}
                </span>
                <span className="text-xs text-themed-secondary block">
                  {formatDuration(item.service.duration)}
                </span>
              </div>
              <span className="text-themed-primary font-medium">
                {formatPriceWithCurrency(parsePrice(item.service.price) * item.quantity, currency)}
              </span>
            </div>
          ))}

          {/* Travel Fee */}
          {applicableTravelFee > 0 && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-themed-secondary">
                {outsideAvailabilityRequested ? 'Estimated Travel Fee' : 'Travel Fee'}
              </span>
              <span className="text-themed-primary font-medium">
                {formatPriceWithCurrency(applicableTravelFee, currency)}
              </span>
            </div>
          )}

          {/* Total */}
          {selectedServices.length > 0 && (
            <div className="border-t border-themed pt-3 space-y-2">
              {applicableTravelFee > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-themed-secondary">Subtotal</span>
                  <span className="text-themed-secondary">
                    {formatPriceWithCurrency(serviceTotal, currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-themed-primary">
                  {isConfirmed ? 'Total' : 'Estimated Total'} &middot; {formatDuration(totalDuration)}
                </span>
                <span className="text-sm font-semibold text-themed-primary">
                  {formatPriceWithCurrency(displayTotal, currency, true)}
                </span>
              </div>
              {!isConfirmed && (
                <p className="text-xs italic text-themed-secondary text-right">
                  Subject to confirmation
                </p>
              )}
            </div>
          )}
        </div>

        {/* Notes Echo */}
        {contactFormData?.additionalNotes?.trim() && (
          <div className="border-t border-themed pt-4 mt-4">
            <div className="flex items-start space-x-3">
              <ChatBubbleLeftIcon className="h-5 w-5 text-themed-secondary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-themed-secondary mb-1">Your Notes</p>
                <p className="text-sm text-themed-primary">{contactFormData.additionalNotes.trim()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Approval Notice (pending only) — themed surface with amber left edge */}
        {!isConfirmed && (
          <div className="border-l-2 border-signal-amber-500 pl-3 py-2 mt-6">
            <div className="flex items-start gap-2">
              <ClipboardDocumentListIcon className="h-4 w-4 text-signal-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {approvalReasons.length > 0 ? (
                  <>
                    <p className="text-sm font-medium text-themed-primary mb-1">
                      Your request includes custom scheduling:
                    </p>
                    <ul className="space-y-0.5">
                      {approvalReasons.map((reason, index) => (
                        <li
                          key={index}
                          className="text-sm text-themed-secondary"
                        >
                          &bull; {reason}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-themed-primary">
                    All appointments require approval before confirmation
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Before Your Appointment — post-booking policies (prep + late arrival) */}
        {getSetPolicies(bookingPage, ["serviceRequirements", "lateArrivalPolicy"]).length > 0 && (
          <div className="border-t border-themed pt-4 mt-6">
            <p className="smallcaps text-themed-secondary mb-3">Before your appointment</p>
            <PolicySections
              bookingPage={bookingPage}
              keys={["serviceRequirements", "lateArrivalPolicy"]}
              dense
            />
          </div>
        )}

        {/* Action Buttons */}
        <ThemedButton
          onClick={handleResetBookingFlow}
          primaryColor={primaryColor}
          variant="primary"
          size="medium"
          className="w-full justify-center mt-6"
        >
          Done
        </ThemedButton>
        {managementUrl && (
          <a
            href={managementUrl}
            className="block text-center text-sm font-medium mt-3 underline text-themed-secondary hover:text-themed-primary transition-colors"
          >
            Manage Your Booking
          </a>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
